import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setCertificateAccess } from "@/lib/issueCertificate";

const BillingContext = createContext(null);
const EMPTY = { access: false, team_access: false, source: "none", plan: null, status: null, preview_usage: {}, subscription: null, seats: 0, seats_used: 0, trial_days: 7 };

export const PLAN_PRICES = { individual: 19, company: 99 };
export const PLAN_PRICES_YEARLY = { individual: 190, company: 990 };
export const TRIAL_DAYS = 7;

// Fires the global upgrade dialog (see components/billing/UpgradeDialog.js).
export const openUpgrade = (feature = "feature") => window.dispatchEvent(new CustomEvent("fab:upgrade", { detail: { feature } }));

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setState(EMPTY); setLoading(false); return; }
    try {
      const { data } = await api.get("/billing/me");
      setState({ ...EMPTY, ...data });
    } catch { setState(EMPTY); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (user === null) return; setLoading(true); refresh(); }, [user, refresh]);
  useEffect(() => { setCertificateAccess(state.access); }, [state.access]);

  // One free scenario + one free quiz: the first key claimed becomes the preview; others are locked.
  const claimPreview = useCallback(async (kind, key) => {
    if (state.access) return { allowed: true, preview: false };
    if (!user) return { allowed: false, preview: true, anonymous: true };
    try {
      const { data } = await api.post("/billing/preview/claim", { kind, key });
      if (data.preview && data.used) setState((s) => ({ ...s, preview_usage: { ...s.preview_usage, [kind]: data.used } }));
      return data;
    } catch { return { allowed: false, preview: true }; }
  }, [state.access, user]);

  const previewLocked = useCallback((kind, key) => !state.access && !!state.preview_usage?.[kind] && state.preview_usage[kind] !== key, [state]);
  const isPreviewItem = useCallback((kind, key) => !state.access && state.preview_usage?.[kind] === key, [state]);

  const value = useMemo(() => ({ ...state, loading, refresh, claimPreview, previewLocked, isPreviewItem, isFree: !state.access }),
    [state, loading, refresh, claimPreview, previewLocked, isPreviewItem]);
  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export const useBilling = () => useContext(BillingContext) || { ...EMPTY, loading: false, refresh: async () => {}, claimPreview: async () => ({ allowed: false }), previewLocked: () => false, isPreviewItem: () => false, isFree: true };
